import * as React from 'react';
import type {CardSpec} from 'common/analyst';
import type {AnalystStreamEvent} from 'common/analystStream';

/**
 * Chat state over /api/analyst/chat (SSE). The Claude API is stateless, so
 * each request replays condensed history — prior questions and final
 * assistant prose; tool traffic is not replayed.
 */

export type AnalystToolActivity = {
  toolCallId: string;
  name: string;
  summary: string;
  done: boolean;
  ok: boolean;
};

export type AnalystTurn = {
  question: string;
  text: string;
  tools: AnalystToolActivity[];
  cards: CardSpec[];
  error: string | null;
  streaming: boolean;
};

export const useAnalystChat = () => {
  const [turns, setTurns] = React.useState<AnalystTurn[]>([]);
  const [busy, setBusy] = React.useState(false);
  const turnsRef = React.useRef(turns);
  turnsRef.current = turns;

  const updateLast = React.useCallback(
    (update: (turn: AnalystTurn) => AnalystTurn) => {
      setTurns((current) => {
        if (current.length === 0) {
          return current;
        }
        const next = [...current];
        next[next.length - 1] = update(next[next.length - 1]);
        return next;
      });
    },
    [],
  );

  const ask = React.useCallback(
    async (question: string) => {
      if (busy || question.trim().length === 0) {
        return;
      }
      setBusy(true);
      // The assistant side of history must include delivered cards — a
      // card-only answer would otherwise vanish, making "break that down"
      // reference nothing.
      const history = turnsRef.current.flatMap((turn) => {
        const cardsNote =
          turn.cards.length > 0
            ? `\n[Delivered cards: ${JSON.stringify(turn.cards)}]`
            : '';
        const assistantText = `${turn.text}${cardsNote}`.trim();
        return [
          {role: 'user', text: turn.question},
          ...(assistantText.length > 0
            ? [{role: 'assistant', text: assistantText}]
            : []),
        ];
      });
      setTurns((current) => [
        ...current,
        {
          question,
          text: '',
          tools: [],
          cards: [],
          error: null,
          streaming: true,
        },
      ]);

      try {
        // Auth rides on the httpOnly session cookie; the dev proxy makes
        // this same-origin.
        const response = await fetch('/api/analyst/chat', {
          method: 'POST',
          credentials: 'include',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({question, history}),
        });
        if (!response.ok) {
          const text = await response.text();
          throw new Error(`HTTP ${response.status}: ${text}`);
        }
        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body');
        }
        const decoder = new TextDecoder();
        let buffer = '';
        for (;;) {
          const {done, value} = await reader.read();
          if (done) {
            break;
          }
          buffer += decoder.decode(value, {stream: true});
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';
          for (const line of lines) {
            if (!line.startsWith('data: ')) {
              continue;
            }
            const data = line.slice(6);
            if (!data) {
              continue;
            }
            const event: AnalystStreamEvent = JSON.parse(data);
            if (event.type === 'text_delta') {
              updateLast((turn) => ({...turn, text: turn.text + event.text}));
            } else if (event.type === 'tool_started') {
              updateLast((turn) => ({
                ...turn,
                tools: [
                  ...turn.tools,
                  {
                    toolCallId: event.toolCallId,
                    name: event.name,
                    summary: event.summary,
                    done: false,
                    ok: true,
                  },
                ],
              }));
            } else if (event.type === 'tool_finished') {
              updateLast((turn) => ({
                ...turn,
                tools: turn.tools.map((tool) =>
                  tool.toolCallId === event.toolCallId
                    ? {...tool, done: true, ok: event.ok}
                    : tool,
                ),
              }));
            } else if (event.type === 'card') {
              updateLast((turn) => ({
                ...turn,
                cards: [...turn.cards, event.card],
              }));
            } else if (event.type === 'error') {
              updateLast((turn) => ({...turn, error: event.message}));
            }
          }
        }
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Analyst chat failed';
        updateLast((turn) => ({...turn, error: message}));
      } finally {
        updateLast((turn) => ({...turn, streaming: false}));
        setBusy(false);
      }
    },
    [busy, updateLast],
  );

  return {turns, busy, ask};
};
